import React from 'react';
import { FiLink, FiImage, FiType, FiInfo, FiCheckCircle, FiClock, FiX, FiLoader, FiAlertTriangle } from 'react-icons/fi';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

// Helper functions (copied from ProfitScanPage for consistency)
const getCompatibilityStyles = (percentage) => {
    const numPercentage = Number(percentage);
    if (isNaN(numPercentage)) {
        return { pathColor: '#9CA3AF', textColor: '#6B7280', trailColor: '#E5E7EB', label: 'نامشخص' };
    }
    if (numPercentage >= 80) return { pathColor: '#10B981', textColor: '#059669', trailColor: '#D1FAE5', label: 'عالی' };
    if (numPercentage >= 50) return { pathColor: '#F59E0B', textColor: '#D97706', trailColor: '#FEF3C7', label: 'متوسط' };
    return { pathColor: '#EF4444', textColor: '#DC2626', trailColor: '#FEE2E2', label: 'ناسازگار' };
};

const getCompatibilityText = (percentage) => {
    const numPercentage = Number(percentage);
    if (isNaN(numPercentage)) return "میزان سازگاری نامشخص است.";
    if (numPercentage >= 80) return "این محصول با شرایط پوست شما کاملاً سازگار است.";
    if (numPercentage >= 50) return "این محصول با شرایط پوست شما تا حد زیادی سازگار است.";
    return "این محصول ممکن است با پوست شما سازگار نباشد. قبل از استفاده، توصیه می‌شود با پزشک یا متخصص مشورت کنید.";
};

const AnalysisResultModal = ({ isOpen, onClose, analysisData, inputInfo, isLoading, error }) => {
    if (!isOpen) return null;

    // Display Logic based on inputInfo (similar to showingAnalysis state)
    let displayProductName = 'محصول بررسی شده';
    let displayProductImage = null; // Not available in modal usually
    let displayProductLink = null;
    let inputMethodIcon = <FiType />;

    if (inputInfo) {
        displayProductName = inputInfo.value || 'تحلیل قبلی';
        if (inputInfo.method === 'link') {
            displayProductName = 'لینک ارائه شده';
            displayProductLink = inputInfo.value;
            inputMethodIcon = <FiLink />;
        } else if (inputInfo.method === 'image') {
            displayProductName = 'تصویر ارائه شده';
            inputMethodIcon = <FiImage />;
        } else {
            inputMethodIcon = <FiType />;
        }
    }

    // Destructure analysis data safely
    const {
        "نام کامل محصول شناسایی شده": identifiedProductName = displayProductName,
        "توضیح محصول": productExplanation = "توضیح محصول ارائه نشده است.",
        "درصد سازگاری": compatibility = 0,
        "دلایل": reasons = [],
        "محصولات تکمیلی": complementaryProducts = [],
        "محصول جایگزین": alternativeProduct = null,
        "نحوه مصرف محصول": usageInstructions = "نحوه مصرف ارائه نشده است."
    } = analysisData || {}; // Use default empty object if analysisData is null

    const compatibilityStyles = getCompatibilityStyles(compatibility);
    const compatibilityText = getCompatibilityText(compatibility);

    return (
        <div
            // Backdrop with animation
            className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ease-out ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={onClose}
            dir="rtl"
        >
            <div
                // Modal container with animation and layout fixes
                className={`bg-white rounded-xl shadow-2xl max-w-xl w-full transition-all duration-300 ease-out flex flex-col transform ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header (Sticky) */}
                 <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-xl">
                    <h2 className="text-lg font-semibold text-gray-800">نتیجه تحلیل محصول</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                        aria-label="بستن"
                    >
                        <FiX size={20} />
                    </button>
                </div>

                {/* Scrollable Modal Content Area */}
                <div className="p-5 md:p-6 flex-grow overflow-y-auto max-h-[calc(90vh-140px)]"> {/* Apply max-height and overflow here */} 
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center min-h-[200px]">
                            <FiLoader size={32} className="animate-spin text-blue-500 mb-3" />
                            <p className="text-gray-600">در حال بارگذاری جزئیات تحلیل...</p>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center min-h-[200px] text-center text-red-600 bg-red-50 p-4 rounded-lg">
                             <FiAlertTriangle className="w-10 h-10 text-red-500 mb-3" />
                            <p className="font-semibold mb-1">خطا در بارگذاری</p>
                             <p className="text-sm">{error}</p>
                        </div>
                    ) : !analysisData ? (
                         <div className="flex flex-col items-center justify-center min-h-[200px] text-center text-gray-500">
                            <p>اطلاعات تحلیل یافت نشد.</p>
                        </div>
                    ) : (
                        // --- Analysis Result Display --- 
                         <div className="space-y-6">
                            {/* 1. Original Input */} 
                             <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 shadow-sm">
                                <h3 className="text-xs font-medium text-gray-500 mb-2">ورودی اصلی:</h3>
                                <div className="flex items-center space-x-3 space-x-reverse">
                                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-xl text-gray-500 flex-shrink-0">
                                        {inputMethodIcon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-sm font-medium text-gray-700 truncate" title={displayProductName}>{displayProductName}</h2>
                                        {displayProductLink && (
                                            <a href={displayProductLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center space-x-1 space-x-reverse mt-0.5">
                                                <FiLink size={12}/> <span>مشاهده لینک</span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* 2. Identified Product & Explanation */} 
                            <div className="bg-white border rounded-xl p-3 shadow-sm">
                                <h2 className="text-md font-semibold mb-2 text-center text-blue-700">{identifiedProductName}</h2>
                                <h3 className="text-sm font-semibold text-gray-600 mb-1 mt-3 pt-2 border-t">توضیحات محصول:</h3>
                                <p className="text-sm text-gray-500 leading-relaxed">{productExplanation}</p>
                            </div>

                            {/* 3. Compatibility Score */} 
                            <div className="text-center p-4 bg-white border rounded-xl shadow-sm">
                                <div className="w-28 h-28 sm:w-32 sm:h-32 mx-auto mb-2">
                                    <CircularProgressbar
                                        value={compatibility}
                                        text={`${compatibility}٪`}
                                        styles={buildStyles({
                                            textSize: '20px',
                                            pathTransitionDuration: 0.5,
                                            pathColor: compatibilityStyles.pathColor,
                                            textColor: compatibilityStyles.textColor,
                                            trailColor: compatibilityStyles.trailColor,
                                        })}
                                    />
                                </div>
                                <p className={`font-semibold text-md`} style={{ color: compatibilityStyles.textColor }}>
                                    سازگاری: {compatibilityStyles.label}
                                </p>
                                <p className="text-xs text-gray-600 mt-1.5">
                                    {compatibilityText}
                                </p>
                            </div>

                            {/* 4. Reasons */} 
                            {reasons && reasons.length > 0 && (
                                <div className="bg-gray-50 text-gray-700 border-l-4 border-gray-400 p-3 rounded-md shadow-sm">
                                    <div className="flex items-center space-x-2 space-x-reverse mb-1.5">
                                        <FiInfo className="text-gray-500 flex-shrink-0" size={18} />
                                        <h3 className="font-semibold text-sm">دلایل بررسی:</h3>
                                    </div>
                                    <ul className="list-disc list-inside space-y-1 text-xs pr-5 text-gray-600">
                                        {reasons.map((reason, index) => (
                                            <li key={index}>{reason}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* 5. Complementary Products */} 
                            {complementaryProducts && complementaryProducts.length > 0 && (
                                <div className="mt-5">
                                    <h3 className="text-sm font-semibold mb-2">محصولات تکمیلی پیشنهادی:</h3>
                                    <div className="flex overflow-x-auto space-x-2 space-x-reverse pb-2">
                                        {complementaryProducts.map((product, index) => (
                                            <div key={index} className="flex-shrink-0 bg-gray-100 rounded-lg p-2 w-[140px] text-xs text-center shadow">
                                                <span className="text-xl mb-1">🧴</span>
                                                <p className="leading-tight">{product}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 6. Alternative Product */} 
                            {alternativeProduct && (
                                <div className="bg-green-50 border-l-4 border-green-400 p-3 text-green-800 rounded-md shadow-sm">
                                    <div className="flex items-center space-x-2 space-x-reverse mb-1">
                                        <FiCheckCircle className="text-green-600 flex-shrink-0" size={18}/>
                                        <h3 className="font-semibold text-sm">محصول جایگزین پیشنهادی:</h3>
                                    </div>
                                    <p className="text-xs mt-1 pl-6">{alternativeProduct}</p>
                                </div>
                            )}

                            {/* 7. Usage Instructions */} 
                             <div className="bg-blue-50 text-blue-900 border-l-4 border-blue-400 p-3 rounded-md shadow-sm">
                                <div className="flex items-center space-x-2 space-x-reverse mb-1">
                                    <FiClock className="text-blue-600 flex-shrink-0" size={18}/>
                                    <h3 className="font-semibold text-sm">روش مصرف:</h3>
                                </div>
                                <p className="text-xs mt-1 pl-6 leading-relaxed">{usageInstructions}</p>
                            </div>
                        </div>
                     // --- End Analysis Result Display --- 
                     )}
                 </div>

                 {/* Modal Footer (Optional, maybe just use header close) */} 
                 {/* <div className="p-4 border-t bg-gray-50 flex justify-end">
                     <button 
                         onClick={onClose}
                         className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-1.5 px-4 rounded-lg text-sm transition duration-150"
                     >
                         بستن
                     </button>
                 </div> */} 
            </div>
        </div>
    );
};

export default AnalysisResultModal; 